import * as React from 'react';
import {FunctionComponent, useEffect, useRef, useState} from 'react';
import {Curve} from '../../../Models/Curve';
import {VictoryAxis, VictoryChart, VictoryLine} from 'victory';
import {VictoryStyleInterface, VictoryStyleObject} from 'victory-core';
import {has} from 'mobx';

export interface GraphableAverageCurve {
    highlighted?: boolean;
    color: string | null;
    curve: Curve;
}

interface OwnProps {
    curve?: Curve;
    referenceCurve?: Curve;
    curves?: GraphableAverageCurve[];
    normalise?: boolean;
    noAxis?: boolean;
    style?: Pick<VictoryStyleInterface, 'parent'> & {
        background?: VictoryStyleObject;
    };
}

const ticks = {
    y: [0, 77, 154, 231, 308, 385, 462, 539, 616, 693],
    x: [0, 20, 40, 60, 80, 100, 120, 140, 160, 180],
};

const styles = {
    line: {
        data: {
            stroke: '#00c9ff',
            strokeWidth: 3,
        },
    },
    refLine: {
        data: {
            stroke: '#ffc107',
            strokeWidth: 2,
        },
    },
    axis: {
        axis: {
            stroke: 'rgba(0, 0, 0, 0.1)',
        },
        grid: {
            stroke: 'rgba(0, 0, 0, 0.1)',
            strokeWidth: 1,
            strokeDasharray: 4,
        },
        tickLabels: {
            fill: '#000',
        },
    },
};

const noAxisStyle = {
    ...styles.axis,
    axis: {stroke: 'transparent'},
    tickLabels: {fill: 'transparent'},
};

export function useIsVisible(ref: any) {
    const [isIntersecting, setIntersecting] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => setIntersecting(entry.isIntersecting));
        if (ref.current) {
            observer.observe(ref.current);
        }
        return () => {
            observer.disconnect();
        };
    }, [ref]);

    return isIntersecting;
}

function normaliseCurve(curve: Curve): Curve {
    const max = curve.reduce((maxForce, point) => (point.y > maxForce ? point.y : maxForce), 0);
    return curve.map((point) => ({x: point.x, y: (100 * point.y) / max}));
}

export const CurveGraph: FunctionComponent<OwnProps> = ({style, noAxis, curve, curves, normalise, referenceCurve}) => {
    const ref = useRef<any>();
    const isVisible = useIsVisible(ref);
    if (curve) {
        return (
            <div ref={ref}>
                <VictoryChart
                    style={style}
                    domainPadding={{y: [-3, 0]}}
                    padding={{top: 10, bottom: 60, left: 50, right: 15}}
                >
                    <VictoryAxis style={noAxis === true ? noAxisStyle : styles.axis} tickValues={ticks.x} />
                    <VictoryAxis
                        style={noAxis === true ? noAxisStyle : styles.axis}
                        dependentAxis
                        orientation="left"
                        tickValues={normalise ? undefined : ticks.y}
                    />
                    {isVisible && referenceCurve && (
                        <VictoryLine
                            style={styles.refLine}
                            data={normalise ? normaliseCurve(referenceCurve) : referenceCurve}
                        />
                    )}
                    {isVisible && <VictoryLine style={styles.line} data={normalise ? normaliseCurve(curve) : curve} />}
                </VictoryChart>
            </div>
        );
    }
    if (curves) {
        const hasHighlightedCurve = curves.some((c) => c.highlighted === true);
        return (
            <div ref={ref}>
                <VictoryChart
                    style={style}
                    domainPadding={{y: [-3, 0]}}
                    padding={
                        noAxis === true
                            ? {top: 10, bottom: 10, left: 15, right: 15}
                            : {top: 10, bottom: 60, left: 50, right: 15}
                    }
                >
                    <VictoryAxis style={noAxis === true ? noAxisStyle : styles.axis} tickValues={ticks.x} />
                    <VictoryAxis
                        style={noAxis === true ? noAxisStyle : styles.axis}
                        dependentAxis
                        orientation="left"
                        tickValues={normalise ? undefined : ticks.y}
                    />
                    {isVisible &&
                        curves.map((c, index) => (
                            <VictoryLine
                                key={index}
                                style={{
                                    data: {
                                        opacity: hasHighlightedCurve && !c.highlighted ? 0.1 : 1,
                                        stroke: c.color ?? '#00c9ff',
                                        strokeWidth: 3,
                                    },
                                }}
                                data={normalise ? normaliseCurve(c.curve) : c.curve}
                            />
                        ))}
                </VictoryChart>
            </div>
        );
    }
    return null;
};
